import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable, Subject, catchError, delay, map, throwError } from 'rxjs';
import { TuwaiqUtil } from 'src/app/core/TuwaiqUtil';
import { ApiConstants } from 'src/app/core/constants/api.constants';
import { FilterDetails } from 'src/app/core/models/filter-details.model';
import { AuthService } from 'src/app/core/services/auth.service';
import { TranslateService } from 'src/app/core/translate/translate.service';
import { SessionExpiredComponent } from 'src/app/shared/components/sessionExpired/sessionExpired/session-expired.component';

@Injectable({
    providedIn: 'root'
})
export class DelegationService {
    isExpiredSession: any;
    showTableLoader = new Subject<boolean>();
    filterServiceList = new BehaviorSubject<FilterDetails[]>([]);
    resetServiceList = new BehaviorSubject<boolean>(false);
    refreshDelegationCounts = new BehaviorSubject<number>(0);
    lang: string;

    // Add a lock to prevent concurrent API calls
    private isCallingApi: boolean = false;

    // Map to convert delegation paths to service status parameters
    private statusMapping = {
        'returned-tickets': 'RETURNED',
        'assigned-to-me': 'ASSIGNED_TO_ME',
        'need-approval': 'NEED_MY_APPROVAL'
    };

    constructor(
        private http: HttpClient,
        private cookieService: CookieService,
        private authService: AuthService,
        public dialog: MatDialog,
        private translateService: TranslateService,
        private router: Router
    ) {
        this.translateService.currentLang$
            .pipe(
                delay(100),
                map(lang => {
                    this.lang = lang;
                })
            )
            .subscribe();
    }

    // Check if delegation is active based on cookie
    isDelegationActive(): boolean {
        return this.cookieService.get('active-delegation') === 'true';
    }

    // Navigate to a service with the appropriate status parameter
    navigateToDelegationService(delegationPath: string): void {
        // Navigate to the delegation path directly
        this.router.navigate(['/delegation', delegationPath]);
    }

    navigateToFirstAvailableMenuItem(): void {
        this.getDelegationTicketCounts().subscribe(counts => {
            const menuItems = this.createDelegationMenuItems(counts);

            if (menuItems.length > 0) {
                // Sort menu items
                const order = {
                    'need-approval': 1,
                    'returned-tickets': 2,
                    'assigned-to-me': 3
                };
                menuItems.sort((a, b) => order[a.path] - order[b.path]);

                // Navigate to the first available menu item
                this.router.navigate(['/delegation', menuItems[0].path]);
            } else {
                // If no menu items, navigate to home
                this.router.navigate(['/home']);
            }
        });
    }

    triggerDelegationCountsRefresh(): void {
        this.refreshDelegationCounts.next(Date.now());
    }

    // Get delegation ticket counts with locking mechanism
    getDelegationTicketCounts(parameters?: HttpParams): Observable<any> {
        const api = ApiConstants.DELEGATION_TICKET_COUNT;

        // If already fetching data, return a new Observable that completes immediately
        if (this.isCallingApi) {
            return new Observable(observer => {
                setTimeout(() => {
                    observer.next({});
                    observer.complete();
                }, 10);
            });
        }

        // Set lock
        this.isCallingApi = true;

        return this.http.get(api, { params: parameters, observe: 'response' }).pipe(
            map(response => {
                // Release lock after response
                this.isCallingApi = false;

                const responseData = response.body['data'];
                TuwaiqUtil.convertObjectKeysToCamelCase(responseData);
                return responseData;
            }),
            catchError(error => {
                // Release lock in case of error
                this.isCallingApi = false;
                return this.errorHandler(error);
            })
        );
    }

    // Convert API response to sidebar menu items
    createDelegationMenuItems(ticketCounts: any): any[] {
        const menuItems = [];

        // Map for translating API keys to display titles
        const menuTitleMap = {
            'returned-tickets': 'delegatedReturnedRequest',
            'assigned-to-me-for-service-management': 'delegatedAssignedRequest',
            'need-my-approval': 'delegatedNeedApprove'
        };

        // Map for paths
        const menuPathMap = {
            'returned-tickets': 'returned-tickets',
            'assigned-to-me-for-service-management': 'assigned-to-me',
            'need-my-approval': 'need-approval'
        };

        // Create menu items from API response
        Object.keys(ticketCounts).forEach((key, index) => {
            menuItems.push({
                id: 100 + index, // Using high IDs to avoid conflicts
                title: menuTitleMap[key] || key,
                path: menuPathMap[key] || key,
                viewType: key,
                status: 0,
                count: ticketCounts[key],
                name: key.toUpperCase().replace(/-/g, '_')
                // icon: 'Clippathgroup.svg'
            });
        });

        // Sort menu items
        return menuItems.sort((a, b) => {
            const order = {
                'need-approval': 1,
                'returned-tickets': 2,
                'assigned-to-me': 3
            };
            return order[a.path] - order[b.path];
        });
    }

    /** Error Handling method */
    errorHandler(errorResponse: HttpErrorResponse) {
        if (errorResponse.status === 401) {
            this.handleSessionExpired();
        } else if (errorResponse.error && errorResponse.error['errors']) {
            return throwError(() => errorResponse.error['errors'][0]);
        }
        return throwError(() => 'An error occurred; please try again later.');
    }

    errorListHandler(errorResponse: HttpErrorResponse) {
        if (errorResponse.status === 401) {
            this.handleSessionExpired();
        } else if (errorResponse.error && errorResponse.error['errors']) {
            return throwError(() => errorResponse.error['errors']);
        }
        return throwError(() => 'An error occurred; please try again later.');
    }

    handleSessionExpired() {
        if (!this.isExpiredSession) {
            this.dialog.open(SessionExpiredComponent, {
                width: '412px',
                disableClose: true
            });
            this.isExpiredSession = true;
        }
    }
}
